'use client';

import { mapCategoryItem, mapEvent, mapPopularTag, mapRelatedEvent } from '@/lib/api/event/mappers';
import type { CategoryItem, Event, PopularTag, RelatedEvent } from '@/lib/api/event/models';
import type {
  CategoryItemDto,
  EventDto,
  EventFilterDto,
  EventsResponseDto,
  PageResponseDto,
  PopularTagDto,
  RelatedEventDto,
} from '@/lib/api/event/types';
import type { QueryParams } from '@/lib/api/httpClient';
import { createHttpClient } from '@/lib/api/httpClient';

const BASE_URL = process.env.NEXT_PUBLIC_API_EVENT_URL;

const http = createHttpClient({ baseUrl: BASE_URL });

export type FetchEventsParams = EventFilterDto & {
  page?: number;
  size?: number;
};

export async function fetchEvents(params?: FetchEventsParams): Promise<Event[]> {
  const dto = await http.get<EventsResponseDto>('/events', { query: params as unknown });
  return (dto.e ?? []).map(mapEvent);
}

export async function fetchEventById(id: string): Promise<Event> {
  const dto = await http.get<EventDto>(`/events/${encodeURIComponent(id)}`);
  return mapEvent(dto);
}

export type SearchEventsBody = EventFilterDto;

export type PageableQuery = {
  page?: number;
  size?: number;
  sort?: string[];
};

export async function searchEvents(
  body: SearchEventsBody,
  pageable?: PageableQuery,
): Promise<Event[]> {
  const dto = await http.post<PageResponseDto<EventDto>>('/events/search', {
    body,
    query: (pageable as unknown as QueryParams) ?? undefined,
  });

  return (dto.content ?? []).map(mapEvent);
}

export async function fetchRelatedEvents(
  eventId: string,
  limit: number = 10,
): Promise<RelatedEvent[]> {
  const response = await http.get<RelatedEventDto[]>(
    `/events/${encodeURIComponent(eventId)}/related`,
    { query: { limit } as unknown as QueryParams },
  );

  if (!response || !Array.isArray(response)) {
    return [];
  }

  return response.map(mapRelatedEvent);
}

export async function fetchCategories(): Promise<CategoryItem[]> {
  const dtos = await http.get<CategoryItemDto[]>('/categories');
  return (Array.isArray(dtos) ? dtos : []).map(mapCategoryItem);
}

export async function fetchPopularTags(categoryId?: string, limit = 20): Promise<PopularTag[]> {
  const query = categoryId ? { categoryId, limit } : { limit };
  const dtos = await http.get<PopularTagDto[]>('/tags/popular', {
    query: query as QueryParams,
  });
  return (Array.isArray(dtos) ? dtos : []).map(mapPopularTag);
}

/**
 * Add event to favorites
 * Endpoint: POST /events/{eventId}/favorite
 */
export async function addEventToFavorites(eventId: string): Promise<void> {
  await http.post<void>(`/events/${encodeURIComponent(eventId)}/favorite`);
}

/**
 * Remove event from favorites
 * Endpoint: DELETE /events/{eventId}/favorite
 */
export async function removeEventFromFavorites(eventId: string): Promise<void> {
  await http.delete<void>(`/events/${encodeURIComponent(eventId)}/favorite`);
}
